/**
 * Scheduling Hooks
 * Custom React hooks for scheduling functionality
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schedulingApi } from '@/lib/api/services/scheduling';
import { toast } from '@/hooks/use-toast';

// ============================================================================
// EVENT TYPES
// ============================================================================

/**
 * Hook to fetch all event types
 */
export function useEventTypes(userId?: string) {
    return useQuery({
        queryKey: ['event-types', userId],
        queryFn: () => schedulingApi.getEventTypes(userId),
        staleTime: 30 * 1000, // 30 seconds
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}

/**
 * Hook to fetch a single event type by ID
 */
export function useEventType(id: string) {
    return useQuery({
        queryKey: ['event-type', id],
        queryFn: () => schedulingApi.getEventTypeById(id),
        enabled: !!id,
    });
}

/**
 * Hook to fetch event type by slug (public)
 */
export function useEventTypeBySlug(slug: string) {
    return useQuery({
        queryKey: ['event-type-slug', slug],
        queryFn: () => schedulingApi.getEventTypeBySlug(slug),
        enabled: !!slug,
    });
}

/**
 * Hook to create a new event type
 */
export function useCreateEventType() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: schedulingApi.createEventType,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['event-types'] });
            toast({ title: 'Event type created successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to create event type', variant: "destructive" });
        },
    });
}

/**
 * Hook to update an event type
 */
export function useUpdateEventType() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) =>
            schedulingApi.updateEventType(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['event-types'] });
            queryClient.invalidateQueries({ queryKey: ['event-type', variables.id] });
            toast({ title: 'Event type updated successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to update event type', variant: "destructive" });
        },
    });
}

/**
 * Hook to delete an event type
 */
export function useDeleteEventType() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: schedulingApi.deleteEventType,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['event-types'] });
            toast({ title: 'Event type deleted successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to delete event type', variant: "destructive" });
        },
    });
}

// ============================================================================
// BOOKINGS
// ============================================================================

/**
 * Hook to fetch all bookings
 */
export function useBookings(eventTypeId?: string) {
    return useQuery({
        queryKey: ['bookings', eventTypeId],
        queryFn: () => schedulingApi.getBookings(eventTypeId),
        staleTime: 30 * 1000, // 30 seconds
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}

export function useBookingsPaged(params: {
    eventTypeId?: string;
    limit?: number;
    offset?: number;
    timeFilter?: 'upcoming' | 'past' | 'all';
    status?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    enabled?: boolean;
} = {}) {
    const {
        eventTypeId,
        limit = 20,
        offset = 0,
        timeFilter = 'all',
        status,
        search,
        dateFrom,
        dateTo,
        enabled = true,
    } = params;

    return useQuery({
        queryKey: ['bookings', 'paged', eventTypeId || '', limit, offset, timeFilter, status || '', search || '', dateFrom || '', dateTo || ''],
        queryFn: () => schedulingApi.getBookingsPaged({ eventTypeId, limit, offset, timeFilter, status, search, dateFrom, dateTo }),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        enabled,
        select: (res: any) => {
            const bookings = Array.isArray(res?.data) ? res.data : [];
            const pagination = res?.pagination || null;
            return { bookings, pagination };
        }
    });
}

/**
 * Hook to fetch scheduling contacts (unique invitees with last meeting date)
 */
export function useSchedulingContacts(params: {
    limit?: number;
    offset?: number;
    search?: string;
    enabled?: boolean;
} = {}) {
    const { limit = 20, offset = 0, search, enabled = true } = params;
    return useQuery({
        queryKey: ['scheduling-contacts', limit, offset, search || ''],
        queryFn: () => schedulingApi.getSchedulingContacts({ limit, offset, search }),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        enabled,
        select: (res: any) => {
            const contacts = Array.isArray(res?.data) ? res.data : [];
            const pagination = res?.pagination || null;
            return { contacts, pagination };
        }
    });
}

/**
 * Hook to fetch a single booking by ID
 */
export function useBooking(id: string) {
    return useQuery({
        queryKey: ['booking', id],
        queryFn: () => schedulingApi.getBookingById(id),
        enabled: !!id,
    });
}

/**
 * Hook to create a new booking
 */
export function useCreateBooking() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: schedulingApi.createBooking,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            toast({ title: 'Booking created successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to create booking', variant: "destructive" });
        },
    });
}

/**
 * Hook to cancel a booking
 */
export function useCancelBooking() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, cancellation_reason }: { id: string; cancellation_reason?: string }) =>
            schedulingApi.cancelBooking(id, cancellation_reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            toast({ title: 'Booking cancelled successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to cancel booking', variant: "destructive" });
        },
    });
}

/**
 * Hook to reschedule a booking
 */
export function useRescheduleBooking() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: { start_time: string; end_time: string } }) =>
            schedulingApi.rescheduleBooking(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['bookings'] });
            toast({ title: 'Booking rescheduled successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to reschedule booking', variant: "destructive" });
        },
    });
}

// ============================================================================
// AVAILABILITY
// ============================================================================

/**
 * Hook to fetch availability settings
 */
export function useAvailabilitySettings(userId?: string, eventTypeId?: string) {
    return useQuery({
        queryKey: ['availability-settings', userId, eventTypeId],
        queryFn: () => schedulingApi.getDefaultAvailability(userId),
        enabled: !!userId,
        staleTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}

/**
 * Hook to create availability settings
 */
export function useCreateAvailabilitySettings() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: schedulingApi.createAvailabilitySetting,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['availability-settings'] });
            toast({ title: 'Availability settings created successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to create availability settings', variant: "destructive" });
        },
    });
}

/**
 * Hook to update availability settings
 */
export function useUpdateAvailabilitySettings() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) =>
            schedulingApi.updateAvailabilitySetting(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['availability-settings'] });
            toast({ title: 'Availability settings updated successfully', variant: "success" });
        },
        onError: (error: any) => {
            toast({ title: error?.response?.data?.error || 'Failed to update availability settings', variant: "destructive" });
        },
    });
}

/**
 * Hook to fetch available time slots
 */
export function useAvailableSlots(eventTypeId: string, startDate: string, endDate: string) {
    return useQuery({
        queryKey: ['available-slots', eventTypeId, startDate, endDate],
        queryFn: () => schedulingApi.getAvailableSlots(eventTypeId, startDate, endDate),
        enabled: !!eventTypeId && !!startDate && !!endDate,
        staleTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}

/**
 * Hook to fetch available time slots for a date range
 */
export function useAvailableSlotsRange(eventTypeId: string, startDate: string, endDate: string, enabled = true) {
    return useQuery({
        queryKey: ['available-slots-range', eventTypeId, startDate, endDate],
        queryFn: () => schedulingApi.getAvailableSlotsRange(eventTypeId, startDate, endDate),
        enabled: !!eventTypeId && !!startDate && !!endDate && enabled,
        staleTime: 60 * 1000, // 1 minute
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        select: (res: any) => res?.data || {},
    });
}

/**
 * Hook to fetch all scheduling users (potential hosts)
 */
export function useSchedulingUsers() {
    return useQuery({
        queryKey: ['scheduling-users'],
        queryFn: () => schedulingApi.getSchedulingUsers(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}

/**
 * Hook to fetch current user's profile and organization info
 */
export function useCurrentSchedulingUser() {
    return useQuery({
        queryKey: ['scheduling-current-user'],
        queryFn: () => schedulingApi.getCurrentUser(),
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
}
